import { Injectable, inject } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { environment } from '../../environments/environment'
import { Dictionary } from '../shared/components/filtering-search-component-base/filtering-search-types'
import { Store } from '@ngrx/store'
import { Observable, of, switchMap, catchError, tap, throwError, finalize } from 'rxjs'
import { MenuDefinition, selectAllMenus, selectListingState, ListingState, loadMenus, resetMegaMenus, resetArticleState } from '../state'
import { logout } from '../state/user.actions'
import { ToastService } from './toast.service'
import { MixpanelService } from './mixpanel.service'
import { User } from '../state/user.model'
import { selectUser } from '../state/user.reducer'
import { FeatueFlagsService } from './featue-flags.service'
import { Router } from '@angular/router'
import { Remarkable } from 'remarkable'
import { SseClient, SseErrorEvent } from 'ngx-sse-client'

export interface AddColumnQuestion {
    text: string;
    table: string;
    historyID: string; // Optional history ID for feedback tracking
}

export interface ChatMessage {
    role: 'user' | 'ai';
    text: string;
    destination?: string;
    area?: string;
    historyID?: number;
    table?: string;
    addColumnQuestions?: AddColumnQuestion[];
    followUpQuestions?: string[];
    elements?: any[]; // Array of autocomplete elements with badges
}

export interface ChatRequest {
    messages: ChatMessage[];
}

export interface ChatStreamEvent {
    role: 'ai';
    text: string;
    overwrite?: boolean;
    isComplete?: boolean; // Flag to indicate if this is the final chunk
    addColumnQuestions?: AddColumnQuestion[]; // Array of follow-up questions with table context
    followUpQuestions?: string[]; // Array of simple follow-up question strings
    historyID?: number; // Optional history ID for feedback tracking
}

@Injectable({
    providedIn: 'root'
})

export class BackEndService {
    private http = inject(HttpClient)
    store = inject<Store<any>>(Store)
    private toastService = inject(ToastService)
    private mixPanelService = inject(MixpanelService)
    private featureFlagsService = inject(FeatueFlagsService)
    private router = inject(Router)
    private markdownRenderer: Remarkable
    private sseClient = inject(SseClient)

    public menus$: Observable<MenuDefinition[]>
    public listingState$: Observable<ListingState>
    public user$: Observable<User | null>

    constructor() {
        this.menus$ = this.store.select(selectAllMenus)
        this.listingState$ = this.store.select(selectListingState)
        this.user$ = this.store.select(selectUser)

        // Initialize markdown renderer
        this.markdownRenderer = new Remarkable({
            html: true,
            breaks: true,
            linkify: true,
            typographer: true,
            linkTarget: '_blank',
        })
        const originalTableOpen = this.markdownRenderer.renderer.rules.table_open;
        const originalTableClose = this.markdownRenderer.renderer.rules.table_close;
        this.markdownRenderer.renderer.rules.table_open = function (tokens, idx, options, env, self) {
            // Call the original renderer to get the default <table> tag
            let html = originalTableOpen.call(this, tokens, idx, options, env, self);

            // Add your custom class
            html = html.replace('<table>', '<table class="table">');

            return html;
        };

    }

    private postToHTTP(url: string, args: Dictionary<any>, includeMenus = true) {
        let menus: MenuDefinition[] = []

        if (includeMenus) {
            this.menus$.subscribe(m => menus = m)
        }

        return this.http.post(url, {
            ...args,
            menus: menus.map(m => ({ id: m.id, version: m.version, area: m.area })),
            featureflags: this.featureFlagsService.getFeatureFlags()
        }, {
            withCredentials: true
        }).pipe(
            switchMap((response: any) => {
                if (response?.loggedIn) {
                    if (response.messages) {
                        for (const message of response.messages) {
                            this.toastService.show(message.text, message.options)
                        }
                    }
                    if (response.menus.length > 0) {
                        this.store.dispatch(loadMenus({ menus: response.menus }))
                    }
                    if (response.closed) {
                        this.router.navigate(['/maintenance'])
                    }

                    return of(response)
                } else {
                    this.mixPanelService.reset()
                    this.store.dispatch(resetMegaMenus())
                    this.store.dispatch(resetArticleState())
                    this.store.dispatch(logout({ error: 'Logged Out' }))
                    return of({})
                }
            })
        )
    }


    article(area: string, id: number, args: Dictionary<any> = {}) {
        return this.postToHTTP(environment.baseUrl + area + '/' + id, args)
    }

    search(search: Dictionary<any>, includeMenus = true) {
        return this.postToHTTP(environment.baseUrl + search['area'], search, includeMenus)
    }

    shareByEmail(area: string, emailAddresses: string[], message: string, articleIDs: string[]) {
        return this.postToHTTP(environment.baseUrl + area + '/share', {
            parameters: {
                emailAddresses,
                message,
                articleIDs,
                origin: window.document.location.origin
            }
        })
    }

    createSavedSearch(savedSearch: Dictionary<any>) {
        return this.postToHTTP(environment.baseUrl + 'user/savedsearches/create', savedSearch).pipe(
            switchMap((response: any) => {
                if (response.user && response.ok) {
                    this.store.dispatch({ type: '[User] Update User', user: response.user })
                }
                return of(response)
            })
        )
    }
    editSavedSearch(id: string, savedSearch: Dictionary<any>) {
        return this.postToHTTP(environment.baseUrl + 'user/savedsearches/' + id, savedSearch).pipe(
            switchMap((response: any) => {
                if (response.user && response.ok) {
                    this.store.dispatch({ type: '[User] Update User', user: response.user })
                }
                return of(response)
            })
        )
    }

    getDocumentAnnotations(id: string) {
        return this.http.get(environment.baseUrl + 'documents/annotations/' + id, {
            withCredentials: true
        })
    }

    deleteSavedSearch(id: string) {
        return this.http.delete(environment.baseUrl + 'user/savedsearches/' + id, {
            withCredentials: true
        }).pipe(
            switchMap((response: any) => {
                if (response.user && response.ok) {
                    this.store.dispatch({ type: '[User] Update User', user: response.user })
                }
                return of(response)
            })
        )
    }

    download(search: Dictionary<any>) {
        return this.postToHTTP(environment.baseUrl + search['area'] + '/download', search)
    }

    downloadprogress(reference: string) {
        return this.http.get(environment.baseUrl + `download/${reference}`, {
            withCredentials: true
        })
    }

    checkJobStatus(token: string) {
        return this.http.get(environment.baseUrl + `job/${token}`, {
            withCredentials: true
        })
    }

    sendMessage(message: Dictionary<any>) {
        return this.http.post(environment.baseUrl + `instruments/send-message`, message, {
            withCredentials: true
        })
    }

    pacerLogin(pacerUsername: string, pacerPassword: string) {
        return this.postToHTTP(environment.baseUrl + 'pacer/login', {
            pacerUsername,
            pacerPassword,
        })
    }
    startDocumentProcess(request: any) {
        return this.postToHTTP(environment.baseUrl + 'pacer/purchase', request)
    }
    startDocketUpdateProcess(request: any) {
        return this.postToHTTP(environment.baseUrl + 'pacer/docketupdate', request)
    }

    validatePacerToken(token: string) {
        return this.postToHTTP(environment.baseUrl + 'pacer/validate', { token })
    }

    updateHasSeen(label: string) {
        return this.postToHTTP(environment.baseUrl + 'user/hasSeen', { label })
    }

    updateHomePageSections(sections: string[]) {
        return this.postToHTTP(environment.baseUrl + 'user/homepage', { sections })
    }
    getSubscriptions() {
        return this.http.get(environment.baseUrl + 'user/subscriptions', {
            withCredentials: true
        })
    }
    getEmailDeliveries() {
        return this.http.get(environment.baseUrl + 'user/email-deliveries', {
            withCredentials: true
        })
    }

    getAPIKey() {
        return this.http.get(environment.baseUrl + 'user/apikey', {
            withCredentials: true
        })
    }

    updateSubscriptions(subscriptions: Dictionary<any>) {
        return this.postToHTTP(environment.baseUrl + 'user/subscriptions', subscriptions)
    }
    sendFeedback(feedback: Dictionary<any>) {
        return this.postToHTTP(environment.baseUrl + 'user/feedback', feedback)
    }
    changePassword(passwordModel: Dictionary<any>) {
        return this.postToHTTP(environment.baseUrl + 'user/password', passwordModel)
    }

    sendPasswordChange() {
        return this.http.get(environment.baseUrl + 'user/passwordchange', {
            withCredentials: true
        })
    }

    validatePublicHash(hash: string = '') {
        return this.http.post(environment.baseUrl + 'validate-public', { hash }, {
            withCredentials: true
        })
    }

    getUnsubscribeInformation(hash: string = '') {
        return this.http.post(environment.baseUrl + 'unsubscribe-info', { hash }, {
            withCredentials: true
        })
    }
    unsubscribe(hash: string = '') {
        return this.http.post(environment.baseUrl + 'unsubscribe', { hash }, {
            withCredentials: true
        })
    }

    getSECDocument(formID: number, docID: number) {
        return this.http.get(environment.baseUrl + `secdocuments/${formID}/${docID}`, {
            withCredentials: true,
        })
    }

    getSECURL(url: string) {
        return this.http.post(environment.baseUrl + `secdocuments`, { url }, {
            withCredentials: true,
        })
    }

    getCreditorPositions(entityID: number, caseIDs: number[]) {
        return this.http.post(environment.baseUrl + `data/creditors2/${entityID}`, caseIDs, {
            withCredentials: true,
        })
    }

    loadChatHistory(areaAndID: any) {
        return this.postToHTTP(environment.baseUrl + 'ai/history', areaAndID);
    }

    getChatHistoryByIndex(index: number, area: string = 'all') {
        return this.postToHTTP(environment.baseUrl + 'ai/history', { index, area });
    }

    getDockets(id: number) {
        return this.http.get(environment.baseUrl + `documents/list/${id}`, {
            withCredentials: true,
        })
    }

    clearChatHistory(areaAndID: any) {
        return this.postToHTTP(environment.baseUrl + 'ai/clear-history', areaAndID);
    }

    addChatHistoryFeedback(historyID: string, feedback: 'positive' | 'negative', message: string) {
        return this.postToHTTP(environment.baseUrl + 'ai/feedback', { historyID, feedback, message });
    }
    ackChatHistoryFeedback(historyID: number) {
        return this.postToHTTP(environment.baseUrl + 'ai/ackfeedback', { historyID });
    }

    startAIProcess(request: any) {
        return this.postToHTTP(environment.baseUrl + 'ai/start', { request })
    }

    checkAIProcesses(tokens: any) {
        return this.postToHTTP(environment.baseUrl + 'ai/status', { tokens })
    }

    getAIAutoCompleteOptions(query: string) {
        return this.postToHTTP(environment.baseUrl + 'ai/autocomplete', { query, limit: 15, timing: [Date.now() / 1000] }) // Increased limit to 15 options
    }

    /**
     * Send a chat message and receive streaming responses via Server-Sent Events
     * @param request - The chat request containing messages
     * @returns Observable that emits streaming chat events
     */
    sendChatMessage(request: ChatRequest): Observable<ChatStreamEvent> {
        console.log('Starting SSE chat with request:', request);

        // Optional analytics tracking
        this.trackStreamingEvent('start', { messageCount: request.messages.length });

        return this.sseClient.stream(
            environment.baseUrl + 'ai/chat',
            {
                keepAlive: false,
                reconnectionDelay: 5000,
                responseType: 'event'
            },
            {
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(request),
                withCredentials: true
            },
            'POST'
        ).pipe(
            switchMap((event: Event) => {
                console.log('SSE event received:', event);

                // Handle different event types
                if (event.type === 'error') {
                    console.error('SSE error event:', event);
                    this.trackStreamingEvent('error', event);
                    const errorEvent = event as SseErrorEvent;
                    return throwError(() => new Error(`SSE connection error: ${errorEvent.message} (Status: ${errorEvent.status})`));
                }

                // Cast to MessageEvent to access data property
                const messageEvent = event as MessageEvent;

                // Skip if no data
                if (!messageEvent.data) {
                    console.log('SSE event with no data, skipping');
                    return of(); // Empty observable - will not emit
                }

                try {
                    const data: ChatStreamEvent = JSON.parse(messageEvent.data);
                    console.log('Parsed SSE data:', data);

                    // Track streaming chunks
                    this.trackStreamingEvent('chunk', {
                        textLength: data.text?.length || 0,
                        isComplete: data.isComplete
                    });

                    // Don't render markdown here for streaming chunks
                    // Let the component handle markdown rendering for the complete message
                    return of(data);
                } catch (error) {
                    console.error('Error parsing SSE message:', error, 'Raw data:', messageEvent.data);
                    this.trackStreamingEvent('error', { parseError: error, rawData: messageEvent.data });
                    // Return error event
                    return of({
                        role: 'ai' as const,
                        text: `Error parsing response: ${error}`,
                        overwrite: true
                    });
                }
            }),
            catchError((error) => {
                console.error('SSE stream error:', error);
                this.trackStreamingEvent('error', { streamError: error });

                // Create detailed error message
                let errorMessage = 'SSE connection failed';
                if (error instanceof Error) {
                    errorMessage = error.message;
                } else if (error.status) {
                    errorMessage = `HTTP ${error.status}: ${error.statusText}`;
                }

                console.error('SSE error details:', {
                    error,
                    message: errorMessage,
                    type: typeof error
                });

                // Rethrow the error to be handled by the caller (expert2 component)
                return throwError(() => new Error(`SSE streaming failed: ${errorMessage}`));
            }),
            finalize(() => {
                // Track completion
                this.trackStreamingEvent('complete', {});
            })
        );
    }

    /**
     * Render markdown text to HTML - public method for component use
     * @param markdownText - The markdown text to render
     * @returns HTML string
     */
    renderMarkdownToHtml(markdownText: string): string {
        try {
            return this.markdownRenderer.render(markdownText, {
                html: true,
                typographer: true,
                linkTarget: "_blank"
            });
        } catch (error) {
            console.error('Error rendering markdown:', error);
            // Fallback to plain text with line breaks
            return markdownText.replace(/\n/g, '<br>');
        }
    }

    /**
     * Enhanced method to render markdown with better error handling and performance
     * @param markdownText - The markdown text to render
     * @param options - Optional rendering options
     * @returns HTML string
     */
    renderMarkdownToHtmlEnhanced(markdownText: string, options?: {
        enableCodeHighlighting?: boolean;
        sanitizeHtml?: boolean;
        maxLength?: number;
    }): string {
        try {
            // Truncate if too long (prevent performance issues)
            if (options?.maxLength && markdownText.length > options.maxLength) {
                markdownText = markdownText.substring(0, options.maxLength) + '...';
            }

            let rendered = this.markdownRenderer.render(markdownText);

            // Basic HTML sanitization if requested
            if (options?.sanitizeHtml) {
                // Remove potentially dangerous HTML elements and attributes
                rendered = rendered.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
                rendered = rendered.replace(/javascript:/gi, '');
                rendered = rendered.replace(/on\w+\s*=/gi, '');
            }

            return rendered;
        } catch (error) {
            console.error('Error rendering markdown:', error);
            // Enhanced fallback with basic formatting preservation
            return markdownText
                .replace(/\n/g, '<br>')
                .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                .replace(/\*(.*?)\*/g, '<em>$1</em>')
                .replace(/`(.*?)`/g, '<code>$1</code>');
        }
    }

    /**
     * Optional: Add streaming analytics/metrics
     * @param eventType - Type of streaming event
     * @param data - Event data
     */
    private trackStreamingEvent(eventType: 'start' | 'chunk' | 'complete' | 'error', data?: any): void {
        // Optional: Send analytics to track streaming performance
        if (!environment.production) {
            console.log(`[Streaming Analytics] ${eventType}:`, data);
            // You could send this to your analytics service
        }
    }

    getUsageOverview(search: Dictionary<any>) {
        return this.postToHTTP(environment.baseUrl + 'my-account/usage/page-views/overview', search, false)
    }

    /**
     * Fallback method: Send a simple non-streaming chat request for testing
     * @param request - The chat request containing messages
     * @returns Observable that emits a single chat response
     */
    sendChatMessageSimple(request: ChatRequest): Observable<ChatStreamEvent> {
        console.log('Using simple chat fallback');
        return this.http.post<any>(environment.baseUrl + 'ai/chat', request, {
            withCredentials: true
        }).pipe(
            switchMap((response: any) => {
                console.log('Simple chat response:', response);

                // Convert response to ChatStreamEvent format
                const event: ChatStreamEvent = {
                    role: 'ai',
                    text: response.text || response.content || response.message || 'Response received but no text found',
                    overwrite: true,
                    isComplete: true // Simple responses are always complete
                };

                // Include addColumnQuestions if present in the response
                if (response.addColumnQuestions && Array.isArray(response.addColumnQuestions)) {
                    event.addColumnQuestions = response.addColumnQuestions;
                    console.log('Simple chat response includes addColumnQuestions:', response.addColumnQuestions);
                }

                // Include followUpQuestions if present in the response
                if (response.followUpQuestions && Array.isArray(response.followUpQuestions)) {
                    event.followUpQuestions = response.followUpQuestions;
                    console.log('Simple chat response includes followUpQuestions:', response.followUpQuestions);
                }

                // Let the component handle markdown rendering for consistency
                return of(event);
            })
        );
    }
}